import { CategoryDataModelType, DataModel } from './DataModel';
import Validator from '../validator/Validator';
import { DropdownOptionType, DropdownOptionValidation } from '../columnsAPI';

export type Option = {
  label: string;
  value: string;
  type: DropdownOptionType;
  alternativeMatches: string[];
  creator?: string;
  baseKey?: string;
  baseKeyCounter?: number;
  baseLabel?: string;
  baseLabelCounter?: number;
  validations?: DropdownOptionValidation[];
};

class CategoryDataModel extends DataModel {
  private options: Option[];
  private isMultiSelection: boolean;
  private allowCustomOptions: boolean;

  constructor({
    description,
    key,
    example,
    label,
    type,
    options,
    validators,
    alternativeMatches,
    columnSize,
    isMultiSelection,
    allowCustomOptions,
  }: {
    description: string;
    key: string;
    example: string;
    label: string;
    type: CategoryDataModelType;
    options: Option[];
    validators?: Validator[];
    alternativeMatches?: string[];
    columnSize: number;
    isMultiSelection: boolean;
    allowCustomOptions?: boolean;
  }) {
    super({
      description,
      key,
      example,
      label,
      type,
      validators,
      alternativeMatches,
      columnSize,
    });
    this.options = options?.map((entry) => ({
      ...entry,
      baseKey: entry?.baseKey || entry.value,
      baseLabel: entry?.baseLabel || entry.label,
      baseKeyCounter: entry.baseKeyCounter || 0,
      baseLabelCounter: entry.baseLabelCounter || 0,
      alternativeMatches: entry.alternativeMatches
        ? [
            ...entry.alternativeMatches.filter((alternativeMatch) => {
              return alternativeMatch !== entry.label;
            }),
            entry.label,
          ]
        : [entry.label],
      validations: entry.validations || [],
    }));
    this.isMultiSelection = isMultiSelection;
    this.allowCustomOptions = allowCustomOptions || false;
  }

  getOptions = () => {
    return this.options;
  };

  setOptions = (options: Option[]) => {
    this.options = options;
  };

  serialize = () => {
    const dataModelJSON = {
      description: this.getDescription(),
      example: this.getExample(),
      isRequired: this.getIsRequired(),
      key: this.getKey(),
      label: this.getLabel(),
      type: this.getType(),
      validators: this.getValidators().map((validator) => {
        return validator.serialize();
      }),
      alternativeMatches: this.getAlternativeMatches(),
      columnSize: this.getColumnSize(),
      outputFormat: this.getOutputFormat(),
      numberFormat: this.getNumberFormat(),
    };

    const options: Option[] = this.getOptions();

    return {
      ...dataModelJSON,
      options,
      isMultiSelection: this.isMultiSelection,
      allowCustomOptions: this.allowCustomOptions,
    };
  };

  clone() {
    const dataModelJSON = this.serialize();
    return new CategoryDataModel({
      description: dataModelJSON.description,
      example: dataModelJSON.example,
      key: dataModelJSON.key,
      label: dataModelJSON.label,
      type: dataModelJSON.type as CategoryDataModelType,
      options: dataModelJSON.options!,
      validators: this.getValidators(),
      alternativeMatches: dataModelJSON.alternativeMatches,
      columnSize: dataModelJSON.columnSize,
      isMultiSelection: dataModelJSON.isMultiSelection,
      allowCustomOptions: dataModelJSON.allowCustomOptions,
    });
  }

  getIsMultiSelection = () => {
    return this.isMultiSelection;
  };

  getAllowCustomOptions = (): boolean => {
    return this.allowCustomOptions;
  };
}

export default CategoryDataModel;
